/**
 * Error class definitions
 *
 * Defines custom error classes for different types of failures that can occur in the SDK.
 * All error classes extend the base LingDatabaseError for consistent error handling.
 */

import { ErrorCodes } from "./codes";

/**
 * Base error class for all SDK-related errors
 */
export class LingDatabaseError extends Error {
  public readonly isLingDatabaseError = true;

  constructor(
    public readonly code: ErrorCodes,
    message: string,
    public readonly originalError?: unknown,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "LingDatabaseError";

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, LingDatabaseError);
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      details: this.details,
      stack: this.stack,
    };
  }
}

export class AuthenticationError extends LingDatabaseError {
  constructor(
    message: string = "Authentication failed",
    originalError?: unknown
  ) {
    super(ErrorCodes.UNAUTHORIZED, message, originalError);
    this.name = "AuthenticationError";
  }
}

export class ValidationError extends LingDatabaseError {
  constructor(field: string, message: string, originalError?: unknown) {
    super(
      ErrorCodes.VALIDATION_ERROR,
      `Validation failed for ${field}: ${message}`,
      originalError
    );
    this.name = "ValidationError";
  }
}

export class NetworkError extends LingDatabaseError {
  constructor(
    message: string = "Network request failed",
    originalError?: unknown
  ) {
    super(ErrorCodes.NETWORK_ERROR, message, originalError);
    this.name = "NetworkError";
  }
}

export class ServiceError extends LingDatabaseError {
  constructor(service: string, message: string, originalError?: unknown) {
    super(
      ErrorCodes.EXTERNAL_API_ERROR,
      `${service} service error: ${message}`,
      originalError
    );
    this.name = "ServiceError";
  }
}
